import React, { useState, useEffect, useRef, useCallback } from "react";
import { useDispatch } from "react-redux";
import ReactMarkdown from "react-markdown";
import { ChatbotApi } from "../Service/api/chatbotApi";
import { Api } from "../Service/api";
import { ACTIONS } from "../redux/actions";
import { HeadersPath } from "../Utils/ScreenPath";
import { madeFinalObject } from "../helper/madeFinalObject";
import { dynamicSequence } from "../helper/dynamicSequence";
import { ErrorToast } from "./Toast";
import PrintModel from "./PrintModel";
import moment from "moment";
import "../Style/Chatbot.css";

// SVG Icons as components
const ChatIcon = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
    </svg>
);

const CloseIcon = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <line x1="18" y1="6" x2="6" y2="18" />
        <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
);

const NewChatIcon = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" />
        <path d="M3 3v5h5" />
    </svg>
);

const SendIcon = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <line x1="22" y1="2" x2="11" y2="13" />
        <polygon points="22 2 15 22 11 13 2 9 22 2" />
    </svg>
);

const RobotIcon = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="11" width="18" height="10" rx="2" />
        <circle cx="12" cy="5" r="2" />
        <path d="M12 7v4" />
        <line x1="8" y1="16" x2="8" y2="16" />
        <line x1="16" y1="16" x2="16" y2="16" />
    </svg>
);

const DocumentIcon = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
        <polyline points="14 2 14 8 20 8" />
        <line x1="16" y1="13" x2="8" y2="13" />
        <line x1="16" y1="17" x2="8" y2="17" />
        <polyline points="10 9 9 9 8 9" />
    </svg>
);

const ImageIcon = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
        <circle cx="8.5" cy="8.5" r="1.5" />
        <polyline points="21 15 16 10 5 21" />
    </svg>
);

const languages = [
    { code: "english", name: "English", flag: "🇬🇧", nativeName: "English" },
    // { code: "hindi", name: "Hindi", flag: "🇮🇳", nativeName: "हिंदी" },
    // { code: "gujarati", name: "Gujarati", flag: "🇮🇳", nativeName: "ગુજરાતી" },
];

// ChatMessage Component - Renders content using ReactMarkdown with click handlers
const ChatMessage = ({ html, markdown, onOptionClick }) => {
    const messageRef = useRef(null);
    const [content, setContent] = useState('');
    const [isInteractiveHtml, setIsInteractiveHtml] = useState(false);

    useEffect(() => {
        // Prioritize markdown (from response field) over html
        const rawContent = markdown || html || '';

        if (rawContent) {
            // Check if content contains interactive HTML buttons that need click handlers
            const hasInteractiveButtons = /class=["'](option-btn|btn-yes|btn-no|btn-generate)["']/i.test(rawContent);
            setIsInteractiveHtml(hasInteractiveButtons);

            if (hasInteractiveButtons) {
                // If it has interactive buttons, keep HTML as is
                setContent(rawContent);
            } else if (markdown) {
                // If markdown is provided, use it directly
                setContent(markdown);
            } else {
                // If only HTML is provided (and not interactive), 
                // remove only the outer <div class="chatbot-message">...</div> if present
                // but preserve inner formatting like \n and **
                let cleanContent = rawContent;

                // Remove outer wrapper if it exists but keep inner content
                const match = cleanContent.match(/<div class=["']chatbot-message["']>([\s\S]*?)<\/div>/i);
                if (match) {
                    cleanContent = match[1];
                }

                setContent(cleanContent.trim());
            }
        }
    }, [html, markdown]);

    useEffect(() => {
        if (messageRef.current && isInteractiveHtml) {
            // For interactive HTML content, use innerHTML and attach click handlers
            messageRef.current.innerHTML = content;

            // Attach click handlers to option buttons
            messageRef.current.querySelectorAll('.option-btn').forEach(btn => {
                btn.onclick = (e) => {
                    e.preventDefault();
                    onOptionClick(btn.dataset.value || btn.textContent);
                };
            });

            // Attach click handlers to yes/no buttons
            messageRef.current.querySelectorAll('.btn-yes').forEach(btn => {
                btn.onclick = (e) => {
                    e.preventDefault();
                    onOptionClick('yes');
                };
            });

            messageRef.current.querySelectorAll('.btn-no').forEach(btn => {
                btn.onclick = (e) => {
                    e.preventDefault();
                    onOptionClick('no');
                };
            });

            // Attach click handlers to generate button
            messageRef.current.querySelectorAll('.btn-generate').forEach(btn => {
                btn.onclick = (e) => {
                    e.preventDefault();
                    onOptionClick('generate');
                };
            });
        }
    }, [content, isInteractiveHtml, onOptionClick]);

    // Render based on content type
    if (isInteractiveHtml) {
        return <div ref={messageRef} className="message-content" />;
    }

    // Use ReactMarkdown for non-HTML content (markdown)
    return (
        <div className="message-content">
            <ReactMarkdown>{content}</ReactMarkdown>
        </div>
    );
};

const ChatbotWidget = () => {
    const dispatch = useDispatch();
    let token = localStorage.getItem("token");
    const [isOpen, setIsOpen] = useState(false);
    const [sessionId, setSessionId] = useState(null);
    const [selectedLanguage, setSelectedLanguage] = useState(null);
    const [messages, setMessages] = useState([]);
    const [inputValue, setInputValue] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const [isTyping, setIsTyping] = useState(false);
    const [isPaperReady, setIsPaperReady] = useState(false);
    const [paperData, setPaperData] = useState(null);
    const [paperConfig, setPaperConfig] = useState(null);
    const [isGenerating, setIsGenerating] = useState(false);
    const [showGenerateButton, setShowGenerateButton] = useState(false);
    const [currentStep, setCurrentStep] = useState(null);

    // Image upload states
    const [selectedImage, setSelectedImage] = useState(null);
    const [imagePreview, setImagePreview] = useState(null);

    // PrintModel states
    const [isModelOpen, setisModelOpen] = useState(false);
    const [modelCall, setModelCall] = useState(false);

    // Draggable and Resizable states
    const [position, setPosition] = useState({ x: null, y: null });
    const [size, setSize] = useState({ width: 580, height: 780 });
    const [isDragging, setIsDragging] = useState(false);
    const [isResizing, setIsResizing] = useState(false);
    const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });

    const messagesEndRef = useRef(null);
    const inputRef = useRef(null);
    const widgetRef = useRef(null);
    const toggleBtnRef = useRef(null);
    const fileInputRef = useRef(null);

    // Min/Max size constraints
    const MIN_WIDTH = 320;
    const MIN_HEIGHT = 400;
    const MAX_WIDTH = 800;
    const MAX_HEIGHT = 1000;

    // Auto-scroll to bottom when messages change
    const scrollToBottom = useCallback(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }, []);

    useEffect(() => {
        scrollToBottom();
    }, [messages, isTyping, scrollToBottom]);

    // Focus input when chat opens and auto-start session if needed
    useEffect(() => {
        if (isOpen) {
            if (!sessionId && !isLoading) {
                // Auto-start English session
                const englishLang = languages.find(l => l.code === "english");
                startSession(englishLang);
            }
            if (sessionId && inputRef.current) {
                inputRef.current.focus();
            }
        }
    }, [isOpen, sessionId, isLoading]);

    // Handle dragging the toggle button
    const handleToggleMouseDown = (e) => {
        // Only start drag on left mouse button
        if (e.button !== 0) return;

        const rect = toggleBtnRef.current.getBoundingClientRect();
        setDragOffset({
            x: e.clientX - rect.left,
            y: e.clientY - rect.top
        });
        setIsDragging(true);
        e.preventDefault();
    };

    // Handle mouse move for dragging
    useEffect(() => {
        const handleMouseMove = (e) => {
            if (isDragging) {
                const newX = e.clientX - dragOffset.x;
                const newY = e.clientY - dragOffset.y;

                // Keep within viewport bounds
                const maxX = window.innerWidth - 60;
                const maxY = window.innerHeight - 60;

                setPosition({
                    x: Math.max(0, Math.min(newX, maxX)),
                    y: Math.max(0, Math.min(newY, maxY))
                });
            }

            if (isResizing && widgetRef.current) {
                const rect = widgetRef.current.getBoundingClientRect();
                const newWidth = e.clientX - rect.left;
                const newHeight = e.clientY - rect.top;

                setSize({
                    width: Math.max(MIN_WIDTH, Math.min(newWidth, MAX_WIDTH)),
                    height: Math.max(MIN_HEIGHT, Math.min(newHeight, MAX_HEIGHT))
                });
            }
        };

        const handleMouseUp = () => {
            if (isDragging) {
                setIsDragging(false);
            }
            if (isResizing) {
                setIsResizing(false);
            }
        };

        if (isDragging || isResizing) {
            document.addEventListener('mousemove', handleMouseMove);
            document.addEventListener('mouseup', handleMouseUp);
        }

        return () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
        };
    }, [isDragging, isResizing, dragOffset]);

    // Handle toggle button click (vs drag)
    const handleToggleClick = (e) => {
        // Only toggle if not dragging
        if (!isDragging) {
            setIsOpen(!isOpen);
        }
    };

    // Handle resize start
    const handleResizeMouseDown = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsResizing(true);
    };

    // Start a new chat session
    const startSession = async (language) => {
        setIsLoading(true);
        setSelectedLanguage(language);

        try {
            const response = await ChatbotApi.startSession(language.code, "openai");

            if (response?.statusCode === 200) {
                const data = JSON.parse(response.data);
                const responseData = data?.data;

                // Store session ID
                setSessionId(responseData?.sessionId);

                // Set initial message with markdown or HTML
                setMessages([
                    {
                        role: "assistant",
                        markdown: responseData?.response,
                        html: responseData?.html,
                        options: responseData?.options || [],
                    },
                ]);

                setIsPaperReady(false);
                setPaperData(null);
                setPaperConfig(null);
                setShowGenerateButton(false);
            } else {
                // Fallback welcome message
                setSessionId(Date.now());
                setMessages([
                    {
                        role: "assistant",
                        html: getWelcomeMessage(language.code),
                    },
                ]);
            }
        } catch (error) {
            console.error("Failed to start session:", error);
            // Use fallback
            setSessionId(Date.now());
            setMessages([
                {
                    role: "assistant",
                    html: getWelcomeMessage(language.code),
                },
            ]);
        } finally {
            setIsLoading(false);
        }
    };

    // Get welcome message based on language
    const getWelcomeMessage = (langCode) => {
        const messages = {
            english: "<p>Hello! 👋 I'm your paper generation assistant. Tell me what kind of paper you need - just mention the standard, subject, and marks.</p>",
            hindi: "<p>नमस्ते! 👋 मैं आपका पेपर जनरेशन असिस्टेंट हूं। मुझे बताएं कि आपको किस तरह का पेपर चाहिए - बस कक्षा, विषय और अंक बताएं।</p>",
            gujarati: "<p>નમસ્તે! 👋 હું તમારો પેપર જનરેશન આસિસ્ટન્ટ છું. મને જણાવો કે તમને કેવા પ્રકારનું પેપર જોઈએ છે - ફક્ત ધોરણ, વિષય અને માર્ક્સ જણાવો.</p>",
        };
        return messages[langCode] || messages.english;
    };

    // Handle option click from HTML buttons
    const handleOptionClick = useCallback(async (value) => {
        if (!sessionId || isTyping) return;
        await sendMessageToApi(value);
    }, [sessionId, isTyping]);

    // Handle image selection
    const handleImageSelect = (e) => {
        const file = e.target.files[0];
        if (!file) return;

        // Validate file type
        const supportedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif', 'image/bmp'];
        if (!supportedTypes.includes(file.type)) {
            ErrorToast('Unsupported image type. Please use JPEG, PNG, WebP, GIF, or BMP.');
            return;
        }

        // Validate file size (max 10MB)
        if (file.size > 10 * 1024 * 1024) {
            ErrorToast('Image too large. Maximum size is 10MB.');
            return;
        }

        setSelectedImage(file);

        // Create preview
        const reader = new FileReader();
        reader.onloadend = () => setImagePreview(reader.result);
        reader.readAsDataURL(file);
    };

    // Remove selected image
    const removeSelectedImage = () => {
        setSelectedImage(null);
        setImagePreview(null);
        if (fileInputRef.current) fileInputRef.current.value = '';
    };

    // Send message to API
    const sendMessageToApi = async (message, imageFile = null) => {
        if ((!message && !imageFile) || !sessionId || isTyping) return;

        // Add user message with optional image preview
        const userMsg = { role: "user", markdown: message || '', html: !message ? '<p><em>📷 Image uploaded</em></p>' : '' };
        if (imageFile && imagePreview) {
            userMsg.imagePreview = imagePreview;
        }
        setMessages((prev) => [...prev, userMsg]);
        setInputValue("");
        setIsTyping(true);

        // Clear image state after capturing preview for the message
        const capturedImage = imageFile;
        removeSelectedImage();

        try {
            let response;
            if (capturedImage) {
                response = await ChatbotApi.sendMessageWithImage(sessionId, message || '', capturedImage);
            } else {
                response = await ChatbotApi.sendMessage(sessionId, message);
            }

            if (response?.statusCode === 200) {
                const data = JSON.parse(response.data);
                const responseData = data?.data;

                // Handle different response types
                if (responseData?.paperGenerated) {
                    // Paper is ready! Store paper data and config
                    setPaperData(responseData?.paperData?.Paper);
                    setPaperConfig(responseData?.paperData?.config || responseData?.selected);
                    setIsPaperReady(true);
                    setShowGenerateButton(false);

                    // Add success message
                    setMessages((prev) => [
                        ...prev,
                        {
                            role: "assistant",
                            markdown: responseData?.response,
                            html: responseData?.html || "<p>✅ Paper generated successfully! Your paper is ready.</p>",
                            paperData: responseData?.paperData,
                        },
                    ]);
                } else if (responseData?.paperReady) {
                    // All details collected, waiting for "generate" command
                    setShowGenerateButton(true);
                    setPaperConfig(responseData?.paperConfig || responseData?.selected);
                    setMessages((prev) => [
                        ...prev,
                        {
                            role: "assistant",
                            markdown: responseData?.response,
                            html: responseData?.html,
                            step: responseData?.step,
                        },
                    ]);
                } else {
                    // Normal message response
                    setMessages((prev) => [
                        ...prev,
                        {
                            role: "assistant",
                            markdown: responseData?.response,
                            html: responseData?.html || "I understand. Let me help you with that.",
                            step: responseData?.step,
                            options: responseData?.options || [],
                        },
                    ]);
                }

                // Update current step
                if (responseData?.step) {
                    setCurrentStep(responseData.step);
                }
            } else {
                // Fallback response
                setMessages((prev) => [
                    { role: "assistant", html: "I understand. Could you please provide more details about the paper you need?" },
                ]);
            }
        } catch (error) {
            console.error("Failed to send message:", error);
            setMessages((prev) => [
                { role: "assistant", html: "Sorry, there was an error. Please try again." },
            ]);
        } finally {
            setIsTyping(false);
        }
    };

    // Send a message from input
    const sendMessage = async () => {
        const message = inputValue.trim();
        if (!message && !selectedImage) return;
        await sendMessageToApi(message || '', selectedImage || null);
    };

    // Handle Enter key press
    const handleKeyPress = (e) => {
        if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            sendMessage();
        }
    };

    // Generate paper - Direct API call to POST /api/chatbot/generate-paper
    const generatePaper = async () => {
        if (!sessionId) return;

        setIsGenerating(true);
        setShowGenerateButton(false);
        dispatch(ACTIONS.startLoading());

        try {
            // Direct API call to generate-paper endpoint
            const response = await ChatbotApi.generatePaper(sessionId);

            if (response?.statusCode === 200) {
                const data = JSON.parse(response.data);
                const responseData = data?.data;

                console.log('Generate paper response:', responseData);

                // Follow the same flow as AutoGenerate.js
                if (responseData?.Paper && responseData?.Paper.length > 0) {
                    // Format paper data using the same helpers as AutoGenerate
                    let FinalObject = madeFinalObject(responseData.Paper, false);
                    let dataObject = dynamicSequence(FinalObject);

                    // Prepare data for PrintModel - same structure as AutoGenerate
                    const paperDisplayData = {
                        institute_logo: '',
                        school_name: '',
                        template_name: '',
                        exam_name: '',
                        chapter_name: '',
                        exam_time: '',
                        exam_marks: responseData?.config?.totalMarks || paperConfig?.totalMarks || '',
                        medium: responseData?.config?.mediumId || paperConfig?.mediumId || '',
                        exam_date: moment(new Date()).format('DD/MM/YYYY'),
                        paper_type: 'auto',
                        genrate_type: 'with section',
                        standard_id: responseData?.config?.standardId || paperConfig?.standardId || '',
                        standard_name: responseData?.config?.standard || paperConfig?.standardLabel || '',
                        subject_id: responseData?.config?.subjectId || paperConfig?.subjectId || '',
                        subject_name: responseData?.config?.subject || paperConfig?.subjectName || '',
                        semester_id: responseData?.config?.semesterId || paperConfig?.semesterId || '',
                        semester_name: responseData?.config?.semester || paperConfig?.semesterNumber || '',
                        sections_info: dataObject,
                    };

                    // Dispatch to Redux (same as AutoGenerate)
                    dispatch(ACTIONS.getSelectedData(paperDisplayData));

                    // Open PrintModel directly (same as AutoGenerate)
                    setisModelOpen(true);
                    setModelCall(true);

                    // Store paper data for reference
                    setPaperData(responseData.Paper);
                    setPaperConfig(responseData.config);
                    setIsPaperReady(true);

                    // Close chatbot after successful generation
                    setIsOpen(false);
                } else {
                    throw new Error("No paper data received");
                }
            } else {
                throw new Error("Failed to generate paper");
            }
        } catch (error) {
            console.error("Failed to generate paper:", error);
            setMessages((prev) => [
                ...prev,
                {
                    html: "❌ Sorry, there was an error generating the paper. Please try again."
                },
            ]);
            setShowGenerateButton(true); // Re-show button on error
        } finally {
            setIsGenerating(false);
            dispatch(ACTIONS.stopLoading());
        }
    };

    // Download paper - Uses the same flow as AutoGenerate
    const downloadPaper = async () => {
        if (!paperData || !paperConfig) {
            ErrorToast("Paper data not available");
            return;
        }

        dispatch(ACTIONS.startLoading());

        try {
            // Use the paper data that was already generated
            let FinalObject = madeFinalObject(paperData, false);
            let dataObject = dynamicSequence(FinalObject);

            if (paperData.length > 0) {
                const data = {
                    institute_logo: '',
                    school_name: '',
                    template_name: '',
                    exam_name: '',
                    chapter_name: '',
                    exam_time: '',
                    exam_marks: paperConfig?.totalMarks || paperConfig?.tmarks || '',
                    medium: paperConfig?.medium?.id || paperConfig?.medium || '',
                    exam_date: moment(new Date()).format('DD/MM/YYYY'),
                    paper_type: 'auto',
                    genrate_type: 'with section',
                    standard_id: paperConfig?.standard?.id || paperConfig?.standard || '',
                    standard_name: paperConfig?.standard?.name || '',
                    subject_id: paperConfig?.subject?.id || paperConfig?.subject || '',
                    subject_name: paperConfig?.subject?.name || paperConfig?.subject?.subject || '',
                    semester_id: paperConfig?.semester?.id || paperConfig?.semester || '',
                    semester_name: paperConfig?.semester?.name || paperConfig?.semester?.semester || '',
                    sections_info: dataObject,
                };

                dispatch(ACTIONS.getSelectedData(data));
                setisModelOpen(true);
                setModelCall(true);
                setIsOpen(!isOpen);
            }
        } catch (e) {
            console.error("Error preparing paper:", e);
            ErrorToast("Error preparing paper for download");
        } finally {
            dispatch(ACTIONS.stopLoading());
        }
    };

    // Handle model close
    const handleModelClose = () => {
        setisModelOpen(false);
    };

    // Reset chat session
    const resetSession = () => {
        setSessionId(null);
        setSelectedLanguage(null);
        setMessages([]);
        setIsPaperReady(false);
        setPaperData(null);
        setPaperConfig(null);
        setInputValue("");
        setShowGenerateButton(false);
        setCurrentStep(null);
        setisModelOpen(false);
        setModelCall(false);
        setSelectedImage(null);
        setImagePreview(null);
    };

    // Toggle chat widget
    const toggleChat = () => {
        setIsOpen(!isOpen);
    };

    // Calculate widget position based on toggle button position
    const getWidgetStyle = () => {
        const style = {
            width: `${size.width}px`,
            height: `${size.height}px`,
        };

        if (position.x !== null && position.y !== null) {
            // Position widget relative to toggle button
            const toggleSize = 60;
            const padding = 16;

            // Determine if widget should appear above or below, left or right
            const spaceBelow = window.innerHeight - position.y - toggleSize;
            const spaceAbove = position.y;
            const spaceRight = window.innerWidth - position.x - toggleSize;
            const spaceLeft = position.x;

            let widgetX, widgetY;

            // Vertical positioning
            if (spaceBelow >= size.height + padding) {
                // Place below
                widgetY = position.y + toggleSize + padding;
            } else if (spaceAbove >= size.height + padding) {
                // Place above
                widgetY = position.y - size.height - padding;
            } else {
                // Center vertically
                widgetY = Math.max(padding, (window.innerHeight - size.height) / 2);
            }

            // Horizontal positioning
            if (spaceRight >= size.width + padding) {
                // Align left edge with toggle
                widgetX = position.x;
            } else if (spaceLeft >= size.width + padding) {
                // Align right edge with toggle
                widgetX = position.x + toggleSize - size.width;
            } else {
                // Center horizontally
                widgetX = Math.max(padding, (window.innerWidth - size.width) / 2);
            }

            // Ensure widget stays within viewport
            widgetX = Math.max(padding, Math.min(widgetX, window.innerWidth - size.width - padding));
            widgetY = Math.max(padding, Math.min(widgetY, window.innerHeight - size.height - padding));

            style.left = `${widgetX}px`;
            style.top = `${widgetY}px`;
            style.right = 'auto';
            style.bottom = 'auto';
        }

        return style;
    };

    // Get toggle button style
    const getToggleBtnStyle = () => {
        if (position.x !== null && position.y !== null) {
            return {
                left: `${position.x}px`,
                top: `${position.y}px`,
                right: 'auto',
                bottom: 'auto',
            };
        }
        return {};
    };

    return (
        <>
            {/* Floating Toggle Button - Draggable */}
            {token && <button
                ref={toggleBtnRef}
                className={`chatbot-toggle-btn ${isOpen ? "open" : ""} ${isDragging ? "dragging" : ""}`}
                onMouseDown={handleToggleMouseDown}
                onClick={handleToggleClick}
                aria-label={isOpen ? "Close chat" : "Open chat"}
                style={getToggleBtnStyle()}
            >
                {!isOpen && <span className="chatbot-beta-tag">beta</span>}
                {isOpen ? <CloseIcon /> : <ChatIcon />}
            </button>}

            {/* Chat Widget - Resizable */}
            <div
                ref={widgetRef}
                className={`chatbot-widget ${isOpen ? "open" : ""}`}
                style={getWidgetStyle()}
            >
                {/* Header */}
                <div className="chatbot-header">
                    <div className="chatbot-avatar">
                        <RobotIcon />
                        <span className="chatbot-status" />
                    </div>
                    <div className="chatbot-header-info">
                        <h3>Paper Assistant</h3>
                        <span>{selectedLanguage ? `Chat in ${selectedLanguage.name}` : "Select language to start"}</span>
                    </div>
                    <div className="chatbot-header-actions">
                        <button
                            className="chatbot-action-btn"
                            onClick={resetSession}
                            title="Start New Conversation"
                        >
                            <NewChatIcon />
                            <span>New</span>
                        </button>
                        <button className="chatbot-action-btn" onClick={toggleChat}>
                            <CloseIcon />
                        </button>
                    </div>
                </div>

                {/* Loading Overlay */}
                {(isLoading || isGenerating) && (
                    <div className="chatbot-loading-overlay">
                        <div className="loading-spinner" />
                        <span>{isGenerating ? "Generating paper..." : "Starting session..."}</span>
                    </div>
                )}

                {/* Language Selection */}
                {!sessionId && !isLoading && (
                    <div className="chatbot-language-select">
                        <h3>Select Language</h3>
                        <p>Choose your preferred language for the chat</p>
                        <div className="language-options">
                            {languages.map((lang) => (
                                <button
                                    key={lang.code}
                                    className="language-btn"
                                    onClick={() => startSession(lang)}
                                >
                                    <span className="language-flag">{lang.flag}</span>
                                    <span>{lang.name}</span>
                                    <span style={{ opacity: 0.6 }}>({lang.nativeName})</span>
                                </button>
                            ))}
                        </div>
                    </div>
                )}

                {/* Chat Messages */}
                {sessionId && (
                    <>
                        <div className="chatbot-messages">
                            {messages.map((msg, index) => (
                                <div key={index} className={`chatbot-message ${msg.role}`}>
                                    {msg.imagePreview && (
                                        <div className="user-image-thumbnail">
                                            <img src={msg.imagePreview} alt="Uploaded" />
                                        </div>
                                    )}
                                    <ChatMessage
                                        markdown={msg.markdown}
                                        html={msg.html}
                                        onOptionClick={handleOptionClick}
                                    />
                                </div>
                            ))}

                            {/* Typing Indicator */}
                            {isTyping && (
                                <div className="chatbot-message assistant">
                                    <div className="typing-indicator">
                                        <span />
                                        <span />
                                        <span />
                                    </div>
                                </div>
                            )}


                            <div ref={messagesEndRef} />
                        </div>

                        {/* Input Area */}
                        <div className="chatbot-input-area">
                            {/* Image Preview Strip */}
                            {imagePreview && (
                                <div className="chatbot-image-preview">
                                    <img src={imagePreview} alt="Preview" />
                                    <span className="chatbot-image-name">{selectedImage?.name}</span>
                                    <button className="chatbot-image-remove" onClick={removeSelectedImage} title="Remove image">
                                        ✕
                                    </button>
                                </div>
                            )}
                            <div className="chatbot-input-wrapper">
                                {/* Hidden file input */}
                                <input
                                    ref={fileInputRef}
                                    type="file"
                                    accept="image/jpeg,image/png,image/webp,image/gif,image/bmp"
                                    style={{ display: 'none' }}
                                    onChange={handleImageSelect}
                                />
                                {/* Image upload button */}
                                <button
                                    className="chatbot-image-btn"
                                    onClick={() => fileInputRef.current?.click()}
                                    disabled={isTyping || isGenerating}
                                    title="Upload question image"
                                >
                                    <ImageIcon />
                                </button>
                                <input
                                    ref={inputRef}
                                    type="text"
                                    className="chatbot-input"
                                    placeholder={getPlaceholder(selectedLanguage?.code)}
                                    value={inputValue}
                                    onChange={(e) => setInputValue(e.target.value)}
                                    onKeyPress={handleKeyPress}
                                    disabled={isTyping || isGenerating}
                                />
                                <button
                                    className="chatbot-send-btn"
                                    onClick={sendMessage}
                                    disabled={(!inputValue.trim() && !selectedImage) || isTyping || isGenerating}
                                >
                                    <SendIcon />
                                </button>
                            </div>

                            {/* Generate Paper Button */}
                            {showGenerateButton && (
                                <button
                                    className="generate-paper-btn"
                                    onClick={generatePaper}
                                    disabled={isGenerating}
                                >
                                    <DocumentIcon />
                                    {isGenerating ? "Generating..." : "Generate Paper 📄"}
                                </button>
                            )}

                        </div>
                    </>
                )}

                {/* Resize Handle */}
                <div
                    className="chatbot-resize-handle"
                    onMouseDown={handleResizeMouseDown}
                    title="Drag to resize"
                />
            </div>

            {/* PrintModel - Same as AutoGenerate */}
            <PrintModel
                isModelOpen={isModelOpen}
                setisModelOpen={setisModelOpen}
                handleModelClose={handleModelClose}
                modelCall={modelCall}
                setModelCall={setModelCall}
                headerPath={HeadersPath.AutoGenerate}
                isEdit={"false"}
            />
        </>
    );
};

// Helper function for placeholders
const getPlaceholder = (langCode) => {
    const placeholders = {
        english: "Type your message...",
        hindi: "अपना संदेश लिखें...",
        gujarati: "તમારો સંદેશ લખો...",
    };
    return placeholders[langCode] || placeholders.english;
};

export default ChatbotWidget;
